import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { siBluesky } from 'simple-icons/icons';

// Section for Utilities and Context
import "./orgprofile.css";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";

// Section for Components
import PeopleFeed from "../components/PeopleFeed";
import DivisionList from "../components/DivisionList";
import OrgActivityFeed from "../components/OrgActivityFeed";
import LeadershipList from "../components/LeadershipList";
import OrgIssueProfile from "../components/OrgIssueProfile";
import DistrictInfoBrowser from "../components/DistrictInfoBrowser";
import EditOrganizationModal from "../components/EditOrganizationModal";
import PageTitle from "../components/PageTitle";
import capitolImage from "../assets/capitol.png";
import { LEADERSHIP_ORG_IDS, SENATE_ORG_ID } from "../utils/leadershipTitles";

const DIVISION_ENABLED_GROUP_TYPES = ["Legislative", "Governor Office", "Agencies", "Local Government"];

// Remembers which tab a user was on for a given org profile, so returning
// within an hour (e.g. after clicking into a division and back) lands on
// the same tab instead of resetting to People.
const TAB_STORAGE_TTL_MS = 60 * 60 * 1000;

function readStoredTab(orgId) {
    if (!orgId) return undefined;
    try {
        const raw = localStorage.getItem(`orgProfileTab:${orgId}`);
        if (!raw) return undefined;
        const { tab, savedAt } = JSON.parse(raw);
        if (!tab || Date.now() - savedAt > TAB_STORAGE_TTL_MS) return undefined;
        return tab;
    } catch {
        return undefined;
    }
}

export default function OrgProfile() {
    const { auth, individualSubscription, accessLoading } = useContext(AuthContext);
    const { id } = useParams();
    const [singleOrganization, setSingleOrganization] = useState({});
    const [orgDivisions, setOrgDivisions] = useState();
    const [loading, setLoading] = useState(false);
    const [filterSelect, setFilterSelect] = useState(() => readStoredTab(id) || "People");
    const canEdit = auth.user.contributor;
    const showDivisions = DIVISION_ENABLED_GROUP_TYPES.includes(singleOrganization.group_type);
    const showIssueProfile = individualSubscription && singleOrganization.group_type !== "Legislative";
    const showLeadership = LEADERSHIP_ORG_IDS.includes(singleOrganization.organization_id);
    const linkedTeam = singleOrganization.TeamAccounts?.[0];


    useEffect(() => {
        // GET SINGLE PROFILE INFO FROM DATABASE FOR PROFILE PAGE
        const fetchOrganization = async () => {
            setLoading(true);
            try {
                const data = await API.getSingleOrganizations(id);
                // console.log("Fetched Profile Data:", data.data);
                setSingleOrganization(data.data);
                setLoading(false);
            } catch (error) {
                console.log("Error fetching single organization:", error);
            }
        };

        fetchOrganization();
    }, [id]);

    const refetchOrganization = async () => {
        // setLoading(true);
        try {
            const data = await API.getSingleOrganizations(id);
            // console.log("Fetched Profile Data:", data.data);
            setSingleOrganization(data.data);
        } catch (error) {
            console.log("Error fetching single organization:", error);
        }
    };


    useEffect(() => {

        const fetchOrgDivisions = async () => {
            if (!singleOrganization.organization_id) return;

            try {
                const data = await API.getDivisionsByOrgId(singleOrganization.organization_id);
                setOrgDivisions(data.data);
            } catch (error) {
                console.log("Error fetching organization divisions for organization profile: ", error);
            }
        }

        fetchOrgDivisions();
    }, [singleOrganization.organization_id]);

    // If the selected tab becomes unavailable for this org (e.g. Divisions
    // isn't applicable to its group_type), fall back to People. Wait until
    // organization data has actually loaded first — otherwise this fires on
    // the very first render (before group_type is known) and clobbers a
    // restored "Divisions" tab.
    useEffect(() => {
        if (!singleOrganization.organization_id) return;
        if (filterSelect === "Divisions" && !showDivisions) {
            setFilterSelect("People");
        }
        if ((filterSelect === "Leadership" || filterSelect === "District Info") && !showLeadership) {
            setFilterSelect("People");
        }
        if (filterSelect === "Issue Profile" && !accessLoading && !showIssueProfile) {
            setFilterSelect("People");
        }
    }, [singleOrganization.organization_id, showDivisions, showLeadership, showIssueProfile, accessLoading, filterSelect]);

    // Persist the selected tab so returning to this org profile within an
    // hour (e.g. after clicking into a division and back) restores it.
    useEffect(() => {
        if (!id) return;
        try {
            localStorage.setItem(`orgProfileTab:${id}`, JSON.stringify({ tab: filterSelect, savedAt: Date.now() }));
        } catch {
            // localStorage unavailable (e.g. private browsing) - ignore
        }
    }, [id, filterSelect]);

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        const cleaned = ('' + phone).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return phone; // fallback if it doesn't match expected pattern
    }

    return (
        <div className="org-profile-page">
            {singleOrganization.organization_name && (
                <PageTitle title={`${singleOrganization.organization_name} | 'elloGovna`} />
            )}
            <div className="row organization-row org-profile-columns">
                <div className="org-profile-sidebar">
                    <img src={singleOrganization.logo || capitolImage}
                        className="org-image"
                        alt={"Logo for " + singleOrganization.organization_name}
                    />

                    <h1 className="org-profile-name mb-0 pb-0">{singleOrganization.organization_name}</h1>

                    <div className="org-profile-badges-row mb-2">
                        <div className="org-profile-badges">
                            {singleOrganization.group_type && (
                                <span className="org-group-type-badge">{singleOrganization.group_type}</span>
                            )}
                            {linkedTeam && (
                                <span className="org-team-badge" style={linkedTeam.tag_color ? { backgroundColor: linkedTeam.tag_color } : undefined}>
                                    {linkedTeam.team_acronym || linkedTeam.team_name}
                                </span>
                            )}
                        </div>

                        <div className="org-profile-socials-link">
                        {singleOrganization.website && (
                            <a className="me-2"
                                href={
                                    singleOrganization.website.startsWith("http") ?
                                    singleOrganization.website :
                                    `https://${singleOrganization.website}`
                                }
                            target="_blank" rel="noopener noreferrer" alt={"Website for " + singleOrganization.organization_name}><i className="bi bi-globe"></i></a>
                        )}
                        {singleOrganization.linkedin_url && (
                            <a className="socials-link" href={singleOrganization.linkedin_url} target="_blank" rel="noopener noreferrer" alt={"LinkedIn for " + singleOrganization.organization_name}><i className="bi bi-linkedin pci-socials me-2" /></a>
                        )}
                        {singleOrganization.facebook_url && (
                            <a className="socials-link" href={singleOrganization.facebook_url} target="_blank" rel="noopener noreferrer" alt={"Facebook for " + singleOrganization.organization_name}><i className="bi bi-facebook pci-socials me-2" target="_blank"/></a>
                        )}
                        {singleOrganization.youtube_url && (
                            <a className="socials-link" href={singleOrganization.youtube_url} target="_blank" rel="noopener noreferrer" alt={"YouTube for " + singleOrganization.organization_name}><i className="bi bi-youtube pci-socials me-2" target="_blank"/></a>
                        )}
                        {singleOrganization.threads_url && (
                            <a className="socials-link" href={singleOrganization.threads_url} target="_blank" rel="noopener noreferrer" alt={"Threads for " + singleOrganization.organization_name}><i className="bi bi-threads pci-socials me-2" target="_blank"/></a>
                        )}
                        {singleOrganization.twitter_url && (
                            <a className="socials-link" href={singleOrganization.twitter_url} target="_blank" rel="noopener noreferrer" alt={"Twitter/X for " + singleOrganization.organization_name}><i className="bi bi-twitter-x pci-socials me-2" target="_blank"/></a>
                        )}
                        {singleOrganization.instagram_url && (
                            <a className="socials-link" href={singleOrganization.instagram_url} target="_blank" rel="noopener noreferrer" alt={"Instagram for " + singleOrganization.organization_name}><i className="bi bi-instagram pci-socials me-2" target="_blank"/></a>
                        )}
                        {singleOrganization.tiktok_url && (
                            <a className="socials-link" href={singleOrganization.tiktok_url} target="_blank" rel="noopener noreferrer" alt={"TikTok for " + singleOrganization.organization_name}><i className="bi bi-tiktok pci-socials me-2"/></a>
                        )}
                        {singleOrganization.bluesky_url && (
                            <a className="socials-link" href={singleOrganization.bluesky_url} target="_blank" rel="noopener noreferrer" alt={"Bluesky for " + singleOrganization.organization_name}>
                                <svg
                                    role="img"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="pci-socials me-2 pci-bluesky-icon"
                                    style={{ height: '1em',  transition: 'fill 0.2s ease' }}
                                >
                                    <title>{siBluesky.title}</title>
                                    <path d={siBluesky.path} />
                                </svg>
                            </a>

                        )}
                        {canEdit && (
                            <i className="bi bi-pencil-square edit-icon-org" alt="Edit Organization Button" data-bs-toggle="modal" data-bs-target="#editOrgProfileModal" />
                        )}
                        </div>
                    </div>

                    <hr className="mt-1"/>

                    {singleOrganization.street_address && (
                        <p className="org-prof-head-text mb-0 pb-0">
                            {singleOrganization.street_address}
                            {singleOrganization.suite_number && `, Suite ${singleOrganization.suite_number}`}<br/>
                            {singleOrganization.city && `${singleOrganization.city}`}
                            {singleOrganization.state && `, ${singleOrganization.state}`}
                            {singleOrganization.zip_code && ` ${singleOrganization.zip_code}`}
                        </p>
                    )}

                    {singleOrganization.lobby_filer_id && (
                        <p className="org-prof-head-text mb-0 pb-0">
                            Cal-Access ID: <a href={`https://cal-access.sos.ca.gov/Lobbying/Firms/Detail.aspx?id=${singleOrganization.lobby_filer_id}`} target="_blank" rel="noopener noreferrer" alt={"Cal-Access Web Page for " + singleOrganization.organization_name}>{singleOrganization.lobby_filer_id}</a>
                        </p>
                    )}

                    {singleOrganization.main_phone && (
                        <p className="org-prof-head-text">{formatPhoneNumber(singleOrganization.main_phone)}</p>
                    )}
                </div>

                <div className={`org-profile-main ${filterSelect === "District Info" && showLeadership ? "org-profile-main--district" : ""}`}>
                    <h6 className="org-selectors">
                        <span className={`me-5 org-feed-selector ${filterSelect === "People" ? "selected" : ""}`} onClick={() => setFilterSelect("People")}>People</span>
                        {showLeadership && (
                            <span className={`me-5 org-feed-selector ${filterSelect === "Leadership" ? "selected" : ""}`} onClick={() => setFilterSelect("Leadership")}>Leadership</span>
                        )}
                        {showDivisions && (
                            <span className={`me-5 org-feed-selector ${filterSelect === "Divisions" ? "selected" : ""}`} onClick={() => setFilterSelect("Divisions")}>Divisions</span>
                        )}
                        {showLeadership && (
                            <span className={`me-5 org-feed-selector ${filterSelect === "District Info" ? "selected" : ""}`} onClick={() => setFilterSelect("District Info")}>District Info</span>
                        )}
                        {showIssueProfile && (
                            <span className={`me-5 org-feed-selector ${filterSelect === "Issue Profile" ? "selected" : ""}`} onClick={() => setFilterSelect("Issue Profile")}>Issue Profile</span>
                        )}
                        <span className={`me-5 org-feed-selector ${filterSelect === "Activity" ? "selected" : ""}`} onClick={() => setFilterSelect("Activity")}>Activity</span>
                    </h6>
                    <hr className="mb-0 "/>

                    {filterSelect === "People" && (
                        <PeopleFeed
                            organization_id={singleOrganization.organization_id}
                        />
                    )}

                    {filterSelect === "Leadership" && showLeadership && (
                        <LeadershipList
                            organization_id={singleOrganization.organization_id}
                        />
                    )}

                    {filterSelect === "District Info" && showLeadership && (
                        <DistrictInfoBrowser
                            prefix={singleOrganization.organization_id === SENATE_ORG_ID ? "SD" : "AD"}
                            count={singleOrganization.organization_id === SENATE_ORG_ID ? 40 : 80}
                        />
                    )}

                    {filterSelect === "Divisions" && showDivisions && !loading && (
                        <DivisionList
                            org_type={singleOrganization.group_type}
                            divisions={orgDivisions}
                        />
                    )}

                    {filterSelect === "Issue Profile" && showIssueProfile && (
                        <OrgIssueProfile
                            organization_id={singleOrganization.organization_id}
                        />
                    )}

                    {filterSelect === "Activity" && (
                        <OrgActivityFeed
                            organization_id={singleOrganization.organization_id}
                        />
                    )}
                </div>
            </div>
            <div className="org-profile-footer"></div>

            {loading && singleOrganization.length === 0 && (
                <div className="d-flex position-absolute top-50 start-50 translate-middle ">
                    <div className="spinner-border loading-spinner" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

           <EditOrganizationModal
                org_data={singleOrganization}
                reRenderOrganization={refetchOrganization}
           />
        </div>
    )
}
